import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { FadeUp, Stagger, StaggerItem } from "../components/Reveal";
import { CaseVisual } from "../components/CaseVisual";
import { CASE_STUDIES } from "../data/content";

export default function CaseStudies() {
    return (
        <div data-testid="case-studies-page">
            <Seo
                title="Our Results | GrowVise"
                description="Case studies from GrowVise and our founder's commercial experience in NZ sports nutrition, retail and distribution: JNX Sports, ATP Science and Roma Blooms."
                path="/our-results"
            />

            <section className="bg-navy-950 pt-32 sm:pt-40 pb-16 sm:pb-20" data-testid="cs-hero">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeUp>
                        <p className="eyebrow">Our Results</p>
                        <h1 className="mt-6 font-serif font-semibold text-white tracking-tight leading-[1.15] text-4xl sm:text-5xl max-w-2xl">
                            Practical strategies behind real commercial results.
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-xl">
                            Each case study sets out the challenge, the commercial strategy and what changed, so you can see how the thinking applies to your business.
                        </p>
                    </FadeUp>
                </div>
            </section>

            <section className="bg-cream section-pad" data-testid="cs-list">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {CASE_STUDIES.map((c) => (
                            <StaggerItem key={c.slug} className="h-full">
                                <Link
                                    to={`/our-results/${c.slug}`}
                                    className="group h-full flex flex-col bg-white border border-navy-900/10 rounded-xl overflow-hidden shadow-[0_1px_3px_rgba(11,21,38,0.06)] hover:border-gold-600/50 hover:shadow-[0_10px_30px_rgba(11,21,38,0.12)] transition-[border-color,box-shadow] duration-300"
                                    data-testid={`cs-card-${c.slug}`}
                                >
                                    <CaseVisual c={c} className="aspect-[16/10] rounded-none" />
                                    <div className="p-7 flex flex-col flex-1">
                                        <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold-700">{c.client}</p>
                                        <h2 className="mt-3 font-serif font-semibold text-ink text-xl leading-snug">{c.title}</h2>
                                        <p className="mt-3 text-sm text-warmgrey leading-relaxed flex-1">{c.cardSummary}</p>
                                        <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy-900 group-hover:text-gold-700 transition-colors duration-200">
                                            Read Case Study <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                                        </span>
                                    </div>
                                </Link>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>

            <section className="bg-navy-900 border-t border-white/10" data-testid="cs-cta">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-16">
                    <FadeUp className="flex-1">
                        <h2 className="font-serif font-semibold text-white tracking-tight leading-snug text-3xl sm:text-4xl">
                            Discuss your growth opportunities.
                        </h2>
                        <p className="mt-4 text-slate-300/85 leading-relaxed max-w-xl">
                            Start with a free 30-minute conversation, or two minutes on the Business Health Check to see where you're constrained.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.1} className="flex flex-wrap gap-4 shrink-0">
                        <Link to="/book-a-conversation" className="btn-gold" data-testid="cs-book-cta">
                            Discuss Your Growth Opportunities
                        </Link>
                        <Link to="/business-health-check" className="btn-outline" data-testid="cs-assessment-cta">
                            Free Business Health Check
                        </Link>
                    </FadeUp>
                </div>
            </section>
        </div>
    );
}
